import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, message, Table, Typography } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { downloadOperatorAdditiveMaterialSpecFile } from '$api/evoAPIs'

const OperatorAdditiveMaterialsTable = ({
  onSelectionChange,
  operatorAdditiveMaterialsData,
  isLoadingOperatorAdditiveMaterials,
  selectedOperatorAdditiveMaterialIds,
  onRowClick,
}) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    }))
  }

  const finalData = useMemo(() => {
    const fetchedItems = operatorAdditiveMaterialsData?.items || []

    const mappedItems = []
    const unmappedItems = []

    fetchedItems.forEach((item) => {
      if (
        selectedOperatorAdditiveMaterialIds instanceof Set &&
        selectedOperatorAdditiveMaterialIds.has(item.id)
      ) {
        mappedItems.push(item)
      } else {
        unmappedItems.push(item)
      }
    })

    const sortedData = [...mappedItems, ...unmappedItems]

    return sortedData.filter((item) =>
      item.material_name.toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [
    operatorAdditiveMaterialsData,
    selectedOperatorAdditiveMaterialIds,
    searchText,
  ])

  const downloadMutation = useMutation({
    mutationFn: downloadOperatorAdditiveMaterialSpecFile,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success(t('download_successful'))
    },
    onError: () => {
      message.error(t('download_failed'))
    },
  })

  const handleDownload = (materialId) => {
    downloadMutation.mutate(materialId)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedKeys) => {
      setSelectedRowKeys(newSelectedKeys)
      if (onSelectionChange) {
        onSelectionChange(new Set(newSelectedKeys))
      }
    },
    preserveSelectedRowKeys: true,
  }

  useEffect(() => {
    const keysFromParent =
      selectedOperatorAdditiveMaterialIds instanceof Set
        ? Array.from(selectedOperatorAdditiveMaterialIds)
        : []
    setSelectedRowKeys(keysFromParent)
  }, [selectedOperatorAdditiveMaterialIds])

  const columns = [
    {
      title: t('operator_additive_materials.header'),
      dataIndex: 'material_name',
      key: 'material_name',
      width: '40%',
      sorter: (a, b) => a.material_name.localeCompare(b.material_name),
      render: (material_name) => (
        <Typography.Text>{material_name}</Typography.Text>
      ),
    },
    {
      title: t('specification_column'),
      dataIndex: 'spec_file',
      key: 'spec_file',
      align: 'center',
      width: '50%',
      render: (spec_file, record) => {
        if (spec_file) {
          return (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownload(record.id)}
              loading={
                downloadMutation.isLoading &&
                downloadMutation.variables === record.id
              }
            >
              {t('download_spec_button')}
            </Button>
          )
        }
        return (
          <Button icon={<DownloadOutlined />} size="small" disabled>
            {t('no_file', 'No File')}
          </Button>
        )
      },
    },
  ]

  return (
    <>
      <Input
        placeholder={t('operator_additive_materials.search_placeholder')}
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <Table
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={finalData}
        loading={isLoadingOperatorAdditiveMaterials}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: finalData?.length || 0,
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        size="small"
        rowClassName={(record) => (record.is_archived ? 'archived-row' : '')}
        onRow={(record) => ({
          onClick: (event) => {
            if (
              event.target.closest('.ant-checkbox-wrapper') ||
              event.target.closest('button')
            ) {
              return
            }
            if (onRowClick) {
              onRowClick(record)
            }
          },
        })}
      />
    </>
  )
}

export default OperatorAdditiveMaterialsTable
